import React, { useMemo, useState } from "react";
import { FieldError } from "react-hook-form";
import { FiLock } from "react-icons/fi";

interface CountryCode {
  code: string;
  label: string;
}

interface PhoneInputProps {
  countries: CountryCode[];
  placeholder?: string;
  value?: string;
  onChange?: (phoneNumber: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
  selectPosition?: "start" | "end";
  error?: FieldError;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  countries,
  placeholder = "+1 (555) 000-0000",
  value = "",
  onChange,
  readOnly = false,
  disabled = false,
  selectPosition = "start",
  error,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  const countryCodes = useMemo(
    () =>
      countries.reduce<Record<string, string>>(
        (acc, { code, label }) => ({
          ...acc,
          [code]: label,
        }),
        {}
      ),
    [countries]
  );

  const isLocked = readOnly || disabled;

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (isLocked) return;

    const newCountry = e.target.value;

    setSelectedCountry(newCountry);

    onChange?.(countryCodes[newCountry] || "");
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return;

    onChange?.(e.target.value);
  };

  const selectClassName = `
    appearance-none
    bg-transparent
    py-3
    pl-3.5
    pr-8
    leading-tight
    text-gray-700
    dark:text-gray-400
    focus:outline-none
    transition-all
    duration-200
    ${isLocked ? "cursor-not-allowed opacity-60" : ""}
  `;

  return (
    <div className="relative flex w-full">
      {/* START SELECT */}
      {selectPosition === "start" && (
        <div className="absolute left-0 top-0 h-full">
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            disabled={isLocked}
            className={`${selectClassName} h-full rounded-l-lg border-0 border-r border-gray-300 dark:border-gray-700`}
          >
            {countries.map((country) => (
              <option
                key={country.code}
                value={country.code}
                className="dark:bg-gray-900"
              >
                {country.code}
              </option>
            ))}
          </select>

          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <svg
              className="stroke-current text-gray-500 dark:text-gray-400"
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}

      {/* INPUT */}
      <input
        type="tel"
        value={value ?? ""}
        readOnly={readOnly}
        disabled={disabled}
        onChange={handlePhoneNumberChange}
        placeholder={placeholder}
        className={`
          flex h-11 w-full appearance-none items-center justify-center
          rounded-lg border p-3 px-4 py-2.5 text-sm outline-none
          transition-all duration-200

          placeholder:text-gray-400

          dark:text-white/90
          dark:placeholder:text-white/30

          ${
            error
              ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
              : `
                border-gray-300
                focus:border-blue-500
                focus:ring-2
                focus:ring-blue-500/30
                dark:border-gray-700
                dark:focus:border-blue-400
                dark:focus:ring-blue-400/30
              `
          }

          ${
            disabled
              ? `
                cursor-not-allowed
                bg-gray-100
                text-gray-400
                dark:bg-white/5
              `
              : readOnly
              ? `
                bg-gray-50
                text-gray-600
                dark:bg-gray-800/50
              `
              : `
                bg-white
                dark:bg-gray-900
              `
          }

          ${selectPosition === "start" ? "pl-24" : "pr-24"}
        `}
      />

      {/* END SELECT */}
      {selectPosition === "end" && (
        <div className="absolute right-0 top-0 h-full">
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            disabled={isLocked}
            className={`${selectClassName} h-full rounded-r-lg border-0 border-l border-gray-300 dark:border-gray-700`}
          >
            {countries.map((country) => (
              <option
                key={country.code}
                value={country.code}
                className="dark:bg-gray-900"
              >
                {country.code}
              </option>
            ))}
          </select>

          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <svg
              className="stroke-current text-gray-500 dark:text-gray-400"
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}

      {/* LOCK ICON */}
      {isLocked && (
        <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center">
          <FiLock size={14} className="text-gray-400" />
        </div>
      )}

      {/* ERROR */}
      {error && (
        <p className="absolute -bottom-5 text-xs text-red-400">
          {error.message}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
